import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Form, Input, Modal, Select, Spin, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast"; 
import { save } from "../../../features/save/saveSlice";
import svg43 from '../../../assets/svg/svg43.svg'
import "./mention.css";
import { fetchDistinctSenderNames } from "../../../features/filter/filterSlice";
import ScheduleMultipleModal from "../group/modal/ScheduleMultipleModal";
import { sendMultipleWhatsApp } from "../../../features/whatsapp/whatsappSlice";
import { extractErrorMessage, normalizeRecipient } from "../../whatsapp/whatsappUtils";
import WhatsAppSenderSelect from "../../../components/WhatsAppSenderSelect";

const { TextArea } = Input;
const CHANNEL_OPTIONS = [
  { label: "SMS", value: "SMS" },
  { label: "WhatsApp", value: "WHATSAPP" },
];

const SmsMultipleModal = ({
  isModalOpen,
  setIsModalOpen, 
  dataSource,
}) => {
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const { saving } = useSelector((state) => state.save);
  const { saving: whatsappSaving } = useSelector((state) => state.whatsapp);
  const { senderNamesData } = useSelector((state) => state.filterSms);
  const [channels, setChannels] = useState(["SMS"]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState();
  const [data, setData] = useState({});
  const smsSelected = channels.includes("SMS");
  const whatsappSelected = channels.includes("WHATSAPP");
  const smsOnly = smsSelected && !whatsappSelected;
  const isSubmitting = saving || whatsappSaving;
  const submitLabel = smsSelected && whatsappSelected
    ? "Send SMS + WhatsApp"
    : whatsappSelected
      ? "Send WhatsApp"
      : "Send SMS";

  const handleNumberChange = (e) => {
    setData((prevData) => ({
      ...prevData,
      mobile: String(e),
    }));
  };
 
  const onChange = async (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const [isModalSchedule, setIsModalSchedule] = useState(false);
  const showModalConfirmation = async () => { 
    if (!formData?.message ) {
      toast.error("Please enter message");
      return;
    }
    if(!formData?.senderId){
      toast.error("Please select sender ID")
      return;
    }
    setIsModalSchedule(true);
  };

  const filteredPhones = dataSource
    ?.filter((item) => item?.phone !== "Click to add contact")
    ?.map((item) => normalizeRecipient(item?.phone)); 
  const onFinish = async (data) => {
    if (!smsSelected && !whatsappSelected) {
      toast.error("Please select at least one channel");
      return;
    }

    if (!formData?.message) {
      toast.error("Please enter message");
      return;
    }

    if (!filteredPhones?.length) {
      toast.error("Please add at least one recipient");
      return;
    }

    if (smsSelected && !formData?.senderId) {
      toast.error("Please select sender ID");
      return;
    }

    let smsSuccess = !smsSelected;
    let whatsappSuccess = !whatsappSelected;
    let smsError = "";
    let whatsappError = "";
    let smsMessage = "";
    let whatsappMessage = "";

    if (smsSelected) {
      const res = await dispatch(
        save({
          url: "api/v2/sms/single-sms",
          message: formData?.message,
          mobile: filteredPhones?.join(","),
          senderId: formData?.senderId,
        })
      );
      smsSuccess = Boolean(res?.payload?.success);
      smsMessage = res?.payload?.messages?.message || "SMS sent successfully";
      smsError = res?.payload?.messages?.message || "SMS send failed";
    }

    if (whatsappSelected) {
      const result = await dispatch(
        sendMultipleWhatsApp({
          recipients: filteredPhones,
          messageType: "TEXT",
          text: formData?.message,
          senderRegistrationId: data?.senderRegistrationId || formData?.senderRegistrationId || undefined,
        })
      );
      whatsappSuccess = sendMultipleWhatsApp.fulfilled.match(result);
      whatsappMessage = result?.payload?.messages?.message || "WhatsApp messages queued successfully";
      whatsappError = extractErrorMessage(result?.payload);
    }

    if (smsSuccess && whatsappSuccess) {
      const successMessage = smsSelected && whatsappSelected
        ? "SMS sent and WhatsApp queued successfully"
        : smsSelected
          ? smsMessage
          : whatsappMessage;
      toast.success(successMessage);
      form.resetFields();
      setFormData({});
      setChannels(["SMS"]);
      setIsModalOpen(false);
      navigate(whatsappSelected && !smsSelected ? "/whatsapp-queue" : "/sent-sms");
      return;
    }

    if (smsSelected && smsSuccess) {
      toast.success(smsMessage);
    } else if (smsSelected) {
      toast.error(smsError);
    }

    if (whatsappSelected && whatsappSuccess) {
      toast.success(whatsappMessage);
    } else if (whatsappSelected) {
      toast.error(whatsappError);
    }

    if (smsSelected && smsSuccess && whatsappSelected && !whatsappSuccess) {
      setChannels(["WHATSAPP"]);
      return;
    }

    if (whatsappSelected && whatsappSuccess && smsSelected && !smsSuccess) {
      setChannels(["SMS"]);
    }
  };

  async function handleSelectChange(value, formName) {
    await setFormData((prevData) => ({
      ...prevData,
      [formName]: value,
    }));
  }

  function fetchDistinctSenderNamesData() {
    dispatch(fetchDistinctSenderNames());
  }

  useEffect(() => {
    fetchDistinctSenderNamesData();
  }, []);

  return (
    <>
      <Modal
        className=""
        title="Message"
        open={isModalOpen}
        onOk={onFinish}
        onCancel={handleCancel}
        width={900}
      >
        <Form
          layout="vertical"
          ref={formRef}
          name="control-ref"
          onFinish={onFinish}
          className="px-[15px]"
          style={{
            maxWidth: "100%",
          }}
          form={form}
        >
          <Form.Item label="Send via">
            <Checkbox.Group
              options={CHANNEL_OPTIONS}
              value={channels}
              onChange={(values) => setChannels(values)}
            />
          </Form.Item>

          {smsSelected && (
            <Form.Item
            name="senderId"
            className=""
            label={
              <span>
                Select sender name <span className="text-[#FF0000]">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please select sender name",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              onChange={(value) => {
                handleSelectChange(value, "senderId");
              }}
              options={
                senderNamesData?.length > 0 &&
                senderNamesData?.map((item) => ({
                  value: item,
                  label: item,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
              onDropdownVisibleChange={() => {
                fetchDistinctSenderNamesData();
              }}
            />
            </Form.Item>
          )}

          <WhatsAppSenderSelect
            visible={whatsappSelected}
            onChange={(value) => {
              handleSelectChange(value, "senderRegistrationId");
            }}
          />

          <Form.Item 
            label={
              <span>
                Custom Message<span className="text-[#FF0000]">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please add a comment",
              },
            ]}
          >
            <TextArea
            required
             name="message"
             value={formData?.message}
              onChange={onChange}
              placeholder="Please add a comment"
              rows={4}
              className="input-textarea"
            />
          </Form.Item>

          <div className="flex justify-between mt-[48px] ">
            <div className="justify-start"></div>
            <div className="justify-end flex items-center mb-[58px] gap-x-5">
              <div className="w-[150px] ">
                <button
                  key="back"
                  type="button"
                  onClick={handleCancel}
                  className="cstm-btn !bg-white !text-[#388E3C] !border !border-[#388E3C]"
                >
                  Cancel
                </button>
              </div>

              <div className="flex">
                <div className="w-[150px]">
                  <button
                    key="submit"
                    type="submit"
                    className={`cstm-btn ${smsOnly ? "!rounded-tr-[0px] !rounded-br-[0px]" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Spin /> : submitLabel}
                  </button>
                </div>
                {smsOnly && (
                  <div className="w-[65px]">
                    <Button
                      onClick={showModalConfirmation}
                      className="cstm-btn   !bg-[#739675] !rounded-tl-[0px] !rounded-bl-[0px]  !h-[2.6rem] "
                    >
                      {saving ? (
                        <Spin />
                      ) : (
                        <Tooltip title="Schedule send">
                          <img className="" src={svg43} alt="svg43" />
                        </Tooltip>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      </Modal>
      

      <ScheduleMultipleModal
        isModalOpen={isModalSchedule}
        setIsModalOpen={setIsModalSchedule}
        title={`Schedule Send`} 
        setIsModalOpenPrev={setIsModalOpen}
        message={formData?.message}
        mobile={filteredPhones?.join(",")}
        senderId={formData?.senderId} 
      />
    </>
  );
};
export default SmsMultipleModal;
